import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Upload, Lock, LogOut, ImageIcon } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND}/api`;
const TOKEN_KEY = "ea_admin_token";

const SLOTS = [
  { key: "hero", label: "Hero image", hint: "The large arched photo at the top of the page" },
  { key: "about", label: "Portrait of Amaléya", hint: "The oval photo in the About section" },
  { key: "practice", label: "Practice photo", hint: "The smaller photo beside your portrait in the About section" },
];

export default function AdminPage() {
  const [token, setToken] = useState(() => sessionStorage.getItem(TOKEN_KEY));
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [files, setFiles] = useState([]);
  const [siteImages, setSiteImages] = useState({ hero: null, about: null });
  const [uploading, setUploading] = useState(false);
  const fileInput = useRef(null);

  const authHeaders = () => ({ Authorization: `Bearer ${token}` });
  const fileUrl = (p) => `${BACKEND}/api/files/${p}`;

  const loadAll = async () => {
    try {
      const [f, s] = await Promise.all([
        axios.get(`${API}/admin/files`, { headers: authHeaders() }),
        axios.get(`${API}/site-images`),
      ]);
      setFiles(f.data);
      setSiteImages(s.data);
    } catch (e) {
      if (e.response?.status === 401) logout();
    }
  };

  useEffect(() => {
    if (token) loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const login = async (e) => {
    e.preventDefault();
    setError("");
    try {
      const { data } = await axios.post(`${API}/admin/login`, { password });
      sessionStorage.setItem(TOKEN_KEY, data.token);
      setToken(data.token);
    } catch (err) {
      setError("That password doesn't match. Please try again.");
    }
  };

  const logout = () => {
    sessionStorage.removeItem(TOKEN_KEY);
    setToken(null);
    setPassword("");
  };

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      await axios.post(`${API}/admin/upload`, form, { headers: authHeaders() });
      toast.success("Photo uploaded.");
      await loadAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed. Please try again.");
    } finally {
      setUploading(false);
      if (fileInput.current) fileInput.current.value = "";
    }
  };

  const setSlot = async (slot, storage_path) => {
    try {
      await axios.post(`${API}/admin/site-images`, { slot, storage_path }, { headers: authHeaders() });
      toast.success(storage_path ? "Page image updated." : "Restored the original image.");
      await loadAll();
    } catch (err) {
      toast.error("Could not update the image. Please try again.");
    }
  };

  if (!token) {
    return (
      <div data-testid="admin-login" className="min-h-screen bg-cream flex items-center justify-center px-6">
        <form
          onSubmit={login}
          className="w-full max-w-sm border border-soil/15 rounded-2xl p-10 bg-cream"
        >
          <Lock className="w-5 h-5 text-amethyst" />
          <h1 className="font-serif text-3xl text-soil mt-4">A quiet corner</h1>
          <p className="text-sm text-clay mt-2">
            This space is just for you, Amaléya. Enter your password to manage
            the photos on your page.
          </p>
          <input
            data-testid="admin-password-input"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Your password"
            className="mt-8 w-full bg-transparent border-0 border-b border-soil/25 px-0 py-3 text-soil placeholder:text-clay/50 focus:outline-none focus:border-amethyst"
          />
          {error && (
            <p data-testid="admin-login-error" className="text-amethyst text-sm mt-3">
              {error}
            </p>
          )}
          <button
            data-testid="admin-login-button"
            type="submit"
            className="mt-8 w-full rounded-full bg-gold text-soil text-xs uppercase tracking-[0.18em] px-8 py-4 hover:bg-amethyst hover:text-cream transition-colors duration-300"
          >
            Enter
          </button>
        </form>
      </div>
    );
  }

  return (
    <div data-testid="admin-panel" className="min-h-screen bg-cream px-6 md:px-12 py-12">
      <div className="max-w-5xl mx-auto">
        <div className="flex items-center justify-between">
          <h1 className="font-serif text-3xl md:text-4xl text-soil">
            Your <span className="italic text-amethyst">photos</span>
          </h1>
          <button
            data-testid="admin-logout-button"
            onClick={logout}
            className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-clay hover:text-amethyst transition-colors duration-300"
          >
            <LogOut className="w-4 h-4" /> Leave
          </button>
        </div>

        <div className="mt-12 grid md:grid-cols-2 gap-8">
          {SLOTS.map((slot) => (
            <div
              key={slot.key}
              data-testid={`admin-slot-${slot.key}`}
              className="border border-soil/15 rounded-2xl p-6"
            >
              <p className="text-xs uppercase tracking-[0.25em] text-amethyst">{slot.label}</p>
              <p className="text-sm text-clay mt-1">{slot.hint}</p>
              <div className="mt-4 aspect-[4/3] rounded-xl overflow-hidden bg-cream-2 flex items-center justify-center">
                {siteImages[slot.key] ? (
                  <img
                    src={fileUrl(siteImages[slot.key])}
                    alt={slot.label}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <span className="text-clay/50 text-sm">Original image in use</span>
                )}
              </div>
              {siteImages[slot.key] && (
                <button
                  data-testid={`admin-slot-${slot.key}-reset`}
                  onClick={() => setSlot(slot.key, null)}
                  className="mt-4 text-xs uppercase tracking-[0.2em] text-clay hover:text-amethyst transition-colors duration-300"
                >
                  Restore original
                </button>
              )}
            </div>
          ))}
        </div>

        <div className="mt-14 border border-soil/15 rounded-2xl p-6 max-w-sm" data-testid="admin-qr-card">
          <p className="text-xs uppercase tracking-[0.25em] text-amethyst">Your QR code</p>
          <p className="text-sm text-clay mt-1">For your poster. It opens your page.</p>
          <img
            src="/qr-code.png"
            alt="QR code linking to your page"
            data-testid="admin-qr-image"
            className="mt-4 w-40 h-40"
          />
          <a
            data-testid="admin-qr-download"
            href="/qr-code.png"
            download="embodied-awakening-qr.png"
            className="mt-4 inline-block text-xs uppercase tracking-[0.2em] text-amethyst hover:text-soil transition-colors duration-300"
          >
            Download
          </a>
        </div>

        <div className="mt-14">
          <button
            data-testid="admin-upload-button"
            onClick={() => fileInput.current?.click()}
            disabled={uploading}
            className="inline-flex items-center gap-3 rounded-full bg-gold text-soil text-xs uppercase tracking-[0.18em] px-8 py-4 hover:bg-amethyst hover:text-cream transition-colors duration-300 disabled:opacity-60"
          >
            <Upload className="w-4 h-4" />
            {uploading ? "Uploading…" : "Upload a photo"}
          </button>
          <input
            ref={fileInput}
            data-testid="admin-file-input"
            type="file"
            accept="image/*"
            onChange={upload}
            className="hidden"
          />
        </div>

        <div className="mt-10 grid grid-cols-2 md:grid-cols-3 gap-6">
          {files.map((f) => (
            <div
              key={f.id}
              data-testid={`admin-file-${f.id}`}
              className="border border-soil/15 rounded-2xl overflow-hidden"
            >
              <div className="aspect-square bg-cream-2">
                <img
                  src={fileUrl(f.storage_path)}
                  alt={f.original_filename}
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-4">
                <p className="text-xs text-clay truncate">{f.original_filename}</p>
                <div className="mt-3 flex flex-wrap gap-3">
                  <button
                    data-testid={`admin-file-${f.id}-set-hero`}
                    onClick={() => setSlot("hero", f.storage_path)}
                    className="text-[11px] uppercase tracking-[0.15em] text-amethyst hover:text-soil transition-colors duration-300"
                  >
                    Use as hero
                  </button>
                  <button
                    data-testid={`admin-file-${f.id}-set-about`}
                    onClick={() => setSlot("about", f.storage_path)}
                    className="text-[11px] uppercase tracking-[0.15em] text-amethyst hover:text-soil transition-colors duration-300"
                  >
                    Use as portrait
                  </button>
                </div>
              </div>
            </div>
          ))}
          {files.length === 0 && (
            <div className="col-span-full flex items-center gap-3 text-clay/60 text-sm py-10">
              <ImageIcon className="w-4 h-4" />
              No photos yet, upload your first one above.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
